import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';

export interface Acao {
  label: string | ((item: any) => string);
  icon: string | ((item: any) => string);
  disabled?: boolean | ((item: any) => boolean);
  hidden?: boolean | ((item: any) => boolean);
  action: (item: any) => void;
}

export interface Coluna {
  name: string;
  label: string;
  acoes?: Acao[];
  truncate?: number;
  format?: (value: string) => string;
  icon?: string | ((item: any) => string);
  iconCondition?: (item: any) => boolean;
  iconClass?: string | ((item: any) => string);
}

export interface FooterColumn {
  name: string;
  total?: boolean;
  totalFormat?: (value: number) => string;
}

export interface TableOptions {
  colunas: Coluna[];
  items?: any[];
  allItems?: any[];
  onSelect?: (item: any) => void;
  footerLabel?: string;
  footerColumns?: FooterColumn[];
}

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent {
  @Input()
  colunas: Coluna[] = [];

  @Input()
  items: any[] = [];

  @Input()
  allItems?: any[] = [];

  @Input()
  selectable?: boolean = false;

  @Input()
  footerLabel?: string = '';

  @Input()
  footerColumns?: FooterColumn[] = [];

  @Output()
  onSelect: EventEmitter<any> = new EventEmitter();

  public dataSource = new MatTableDataSource<any>();
  public displayedColumns: string[] = [];

  constructor() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['colunas']) {
      this.displayedColumns = this.colunas.map((coluna) => coluna.name);

      if(this.selectable) {
        this.displayedColumns = ['select'].concat(this.displayedColumns);
      }
    }

    if (changes['items']) {
      this.dataSource.data = this.items;
    }
  }

  protected getAcaoLabel(item: any, acao: Acao): string {
    if (typeof acao.label === 'function') {
      return acao.label(item);
    }

    return acao.label;
  }

  protected getAcaoIcon(item: any, acao: Acao): string {
    if (typeof acao.icon === 'function') {
      return acao.icon(item);
    }

    return acao.icon;
  }

  protected getAcaoDisabled(item: any, acao: Acao): boolean {
    if (typeof acao.disabled === 'function') {
      return acao.disabled(item);
    }
    return !!acao.disabled;
  }

  protected getAcaoHidden(item: any, acao: Acao): boolean {
    if (typeof acao.hidden === 'function') {
      return acao.hidden(item);
    }
    return !!acao.hidden;
  }

  protected hasAcoes(item: any, acoes: Acao[]): boolean {
    if (!acoes || acoes.length === 0) return false;
    return acoes.some(acao => !this.getAcaoHidden(item, acao));
  }

  protected getIcon(item: any, coluna: Coluna): string | null {
    if (!coluna.icon) {
      return null;
    }

    if (coluna.iconCondition && !coluna.iconCondition(item)) {
      return null;
    }

    if (typeof coluna.icon === 'function') {
      return coluna.icon(item);
    }

    return coluna.icon;
  }

  protected getIconClass(item: any, coluna: Coluna): string {
    const icon = this.getIcon(item, coluna);

    if (!icon) {
      return '';
    }

    let defaultClass = 'table-status-icon';

    if (coluna.iconClass) {
      const customClass = typeof coluna.iconClass === 'function' ? coluna.iconClass(item) : coluna.iconClass;
      if (customClass) {
        defaultClass += ` ${customClass}`;
      }
    }

    return defaultClass;
  }

  protected getFooterColumn(colunaName: string): FooterColumn | undefined {
    return this.footerColumns?.find(fc => fc.name === colunaName);
  }

  protected getTotal(coluna: FooterColumn): string {
    const dataSource = this.allItems && this.allItems.length > 0 ? this.allItems : this.items;

    if (!dataSource || dataSource.length === 0) {
      return '';
    }

    const total = dataSource.reduce((acc: number, item: any) => {
      const value = coluna.name.trim().split('.').reduce((acc: any, curr: string) => acc ? acc[curr] : null, item);

      const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d.,-]/g, '')) : value;

      return acc + (isNaN(numValue) ? 0 : numValue);
    }, 0);

    if (coluna.totalFormat) {
      return coluna.totalFormat(total);
    }

    return total.toString();
  }

  protected getValue(element: any, coluna: Coluna, ignoreTruncate?: boolean): string {
    let value = '';

    /** Concatena valores de forma recursiva */
    if (coluna.name.includes('+')) {
      for (const name of coluna.name.split('+').map(c => c.trim())) {
        value += this.getValue(element, { ...coluna, name }, true);
      }
    } 
    /** Busca o primeiro valor válido de forma recursiva */
    else if (coluna.name.includes('||')) {
      for (const name of coluna.name.split('||').map(c => c.trim())) {
        value = this.getValue(element, { ...coluna, name }, true);
        if (value) break;
      }
    } 
    /** Soma os valores de um vetor -> sum(valores.valor) */
    else if (coluna.name.includes('sum')) {
      const [arrKey, valKey] = coluna.name.match(/sum\((.*)\)/)![1].split('.');
      value = element[arrKey].reduce((acc: number, curr: any) => acc + curr[valKey], 0);
    }
    else {
      value = coluna.name.trim().split('.').reduce((acc, curr) => acc ? acc[curr] : null, element);
    }

    if (coluna.format) {
      value = coluna.format(value);
      return value;
    }

    if(!value) return '';

    if (coluna.truncate && value.length > coluna.truncate && !ignoreTruncate) {
      value = value.substring(0, coluna.truncate) + '...';
    }

    return value;
  }

  protected handleSelect(item: any) {
    this.onSelect.emit(item);
  }
}
